import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Tv, Radio, StopCircle, Trash2, Plus, Save, Film, Wifi, WifiOff, RefreshCw, Loader2, Pencil, Bell } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";
import { ImageUpload } from "@/components/ImageUpload";
import { NotifyMembersDialog } from "@/components/NotifyMembersDialog";
import { VimeoLivePanel } from "./VimeoLivePanel";
import { CloudflareStreamPanel } from "./CloudflareStreamPanel";

const SOURCE_TYPES = [
  { v: "youtube", label: "YouTube (URL / video ID)" },
  { v: "vimeo", label: "Vimeo Live (event / video link)" },
  { v: "hls", label: "HLS stream (.m3u8)" },
  { v: "mp4", label: "MP4 / direct URL" },
  { v: "iframe", label: "Embed / iframe URL" },
  { v: "external", label: "Other provider URL" },
];
const BLANK = { title: "", sourceType: "youtube", sourceValue: "", channelKey: "", programId: "", programName: "", isPremium: true, autoVod: true, status: "live", startAt: "", endAt: "", thumbnail: "", schedule: "" };

const Select = ({ label, value, onChange, children, ...p }) => (
  <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <select value={value} onChange={onChange} {...p} className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm">{children}</select>
  </label>
);

export default function AdminLive() {
  const { data: streams, loading, reload } = useCrud(endpoints.adminLiveStreams);
  const [channels, setChannels] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [chForm, setChForm] = useState({ channelName: "", handle: "", channelId: "", thumbnail: "" });
  const [health, setHealth] = useState({});
  const [checking, setChecking] = useState(false);
  const [notifyFor, setNotifyFor] = useState(null);

  const checkHealth = async () => {
    setChecking(true);
    try {
      const { data } = await endpoints.adminLiveStreamsHealth();
      const map = {};
      (data.items || []).forEach((x) => { map[x.id] = x; });
      setHealth(map);
    } catch (e) { handleErr(e); } finally { setChecking(false); }
  };
  useEffect(() => { if (streams.length) checkHealth(); /* eslint-disable-next-line */ }, [streams.length]);

  const loadAux = () => {
    endpoints.adminChannels().then((r) => { setChannels(r.data); if (!form.channelKey && r.data[0]) setForm((f) => ({ ...f, channelKey: r.data[0].key })); }).catch(() => {});
    endpoints.adminPrograms().then((r) => setPrograms(r.data)).catch(() => {});
  };
  useEffect(() => { loadAux(); /* eslint-disable-next-line */ }, []);

  const go = async () => {
    if (!form.title || !form.sourceValue) { toast.error("Title and source are required"); return; }
    const prog = programs.find((p) => p.id === form.programId);
    const chan = channels.find((c) => c.key === form.channelKey);
    const payload = { ...form, programName: prog?.name || "", channelName: chan?.channelName || "" };
    try {
      if (editingId) {
        await endpoints.adminUpdateLiveStream(editingId, payload);
        toast.success("Live show updated");
      } else {
        await endpoints.adminCreateLiveStream(payload);
        toast.success(form.status === "live" ? "Now live!" : "Live stream scheduled");
      }
      setForm({ ...BLANK, channelKey: form.channelKey }); setShowForm(false); setEditingId(null); reload();
    } catch (e) { handleErr(e); }
  };
  const edit = (s) => {
    setEditingId(s.id);
    setForm({
      title: s.title || "", sourceType: s.sourceType || "youtube",
      sourceValue: s.sourceValue || s.sourceUrl || "", channelKey: s.channelKey || "",
      programId: s.programId || "", programName: s.programName || "",
      isPremium: s.isPremium !== false, autoVod: s.autoVod !== false,
      status: s.status === "scheduled" ? "scheduled" : "live",
      startAt: s.startAt || "", endAt: s.endAt || "", thumbnail: s.thumbnail || "", schedule: s.schedule || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setForm(BLANK); setEditingId(null); setShowForm(false); };
  const end = async (s) => {
    try { await endpoints.adminEndLiveStream2(s.id, { createVod: true, publish: true, category: "Highlights" }); toast.success("Ended · published to Highlights VOD"); reload(); }
    catch (e) { handleErr(e); }
  };
  const del = async (id) => { try { await endpoints.adminDeleteLiveStream(id); toast.success("Deleted"); reload(); } catch (e) { handleErr(e); } };

  const addChannel = async () => {
    if (!chForm.channelName) { toast.error("Channel name required"); return; }
    try { await endpoints.adminCreateChannel(chForm); toast.success("Channel added"); setChForm({ channelName: "", handle: "", channelId: "", thumbnail: "" }); loadAux(); }
    catch (e) { handleErr(e); }
  };
  const saveChannel = async (c) => { try { await endpoints.adminUpdateChannel(c.id, { channelName: c.channelName, handle: c.handle, channelId: c.channelId, thumbnail: c.thumbnail }); toast.success("Saved"); loadAux(); } catch (e) { handleErr(e); } };
  const delChannel = async (c) => { if (!c.id || c.id === c.key) { toast.error("Default channel — add a custom one to manage."); return; } try { await endpoints.adminDeleteChannel(c.id); toast.success("Channel removed"); loadAux(); } catch (e) { handleErr(e); } };

  return (
    <div>
      <PageHeader title="Live Control" desc="Multiple simultaneous live streams · any provider · scheduled → live → ended → VOD.">
        <Btn onClick={() => { setEditingId(null); setForm(BLANK); setShowForm((s) => !s); }} data-testid="live-add-btn"><Radio className="w-4 h-4" /> New live stream</Btn>
      </PageHeader>
      <div className="mb-6"><CloudflareStreamPanel /></div>

      {showForm && (
        <Card className="p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="live-title-input" />
          <Select label="Source type" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} data-testid="live-sourcetype-select">
            {SOURCE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </Select>
          <Field label="Source (URL / video ID)" value={form.sourceValue} onChange={(e) => setForm({ ...form, sourceValue: e.target.value })} data-testid="live-source-input" placeholder={form.sourceType === "vimeo" ? "https://vimeo.com/event/123456 — or create one below" : "https://… or video ID"} />
          {form.sourceType === "vimeo" && <VimeoLivePanel title={form.title} onUse={(u) => setForm((f) => ({ ...f, sourceValue: u }))} />}
          <Select label="Channel" value={form.channelKey} onChange={(e) => setForm({ ...form, channelKey: e.target.value })} data-testid="live-channel-select">
            {channels.map((c) => <option key={c.key} value={c.key}>{c.channelName}</option>)}
          </Select>
          <Select label="Program (optional)" value={form.programId} onChange={(e) => setForm({ ...form, programId: e.target.value })}>
            <option value="">— None —</option>
            {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="live-status-select">
            <option value="live">Live now</option>
            <option value="scheduled">Scheduled</option>
          </Select>
          <ImageUpload label="Thumbnail" value={form.thumbnail} onChange={(url) => setForm({ ...form, thumbnail: url })} testid="live-thumb" />
          <Field label="Start (ISO, optional)" value={form.startAt} onChange={(e) => setForm({ ...form, startAt: e.target.value })} placeholder="2026-06-10T18:00:00Z" />
          <Field label="End (ISO, optional)" value={form.endAt} onChange={(e) => setForm({ ...form, endAt: e.target.value })} placeholder="2026-06-10T20:00:00Z" />
          <Field label="Schedule note (optional)" value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="e.g. Every Sat 6PM" />
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer mt-6">
            <input type="checkbox" checked={form.isPremium} onChange={(e) => setForm({ ...form, isPremium: e.target.checked })} data-testid="live-premium-toggle" className="w-4 h-4 accent-[#E11D2A]" />
            Subscriber-only (premium)
          </label>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer mt-6">
            <input type="checkbox" checked={form.autoVod} onChange={(e) => setForm({ ...form, autoVod: e.target.checked })} data-testid="live-autovod-toggle" className="w-4 h-4 accent-[#E11D2A]" />
            Auto-publish to Highlights when it ends
          </label>
          <div className="flex items-end gap-2"><Btn onClick={go} data-testid="live-create-btn" className="flex-1"><Radio className="w-4 h-4" /> {editingId ? "Save changes" : (form.status === "live" ? "Go live" : "Schedule")}</Btn>
            {editingId && <Btn variant="ghost" onClick={cancelEdit} data-testid="live-cancel-btn">Cancel</Btn>}</div>
        </Card>
      )}

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold">Live & scheduled streams</h3>
        {streams.length > 0 && (
          <Btn variant="ghost" onClick={checkHealth} disabled={checking} data-testid="live-health-check-btn">
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Check status
          </Btn>
        )}
      </div>
      {loading ? <Loading /> : streams.length === 0 ? <Empty text="No live streams yet." /> : (
        <Card className="divide-y divide-[#273042] mb-8">
          {streams.map((s) => (
            <div key={s.id} data-testid={`live-row-${s.id}`} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white text-sm font-semibold truncate">{s.title} {s.isPremium && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#2563EB] text-white text-[9px] font-extrabold">PREMIUM</span>}</div>
                <div className="text-xs text-slate-400">{s.channelName || s.channelKey} · {s.sourceType} · {s.isLive ? <span className="text-[#EF4444] font-bold">LIVE</span> : s.status}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {(() => {
                  const h = health[s.id];
                  if (!h) return null;
                  if (h.online === true) return <span data-testid={`live-health-${s.id}`} className="flex items-center gap-1 text-[10px] font-bold text-emerald-400"><Wifi className="w-3.5 h-3.5" /> Online</span>;
                  if (h.online === false) return <span data-testid={`live-health-${s.id}`} title={h.error || (h.statusCode ? `HTTP ${h.statusCode}` : "Unreachable")} className="flex items-center gap-1 text-[10px] font-bold text-red-400"><WifiOff className="w-3.5 h-3.5" /> Offline</span>;
                  return <span data-testid={`live-health-${s.id}`} className="text-[10px] font-semibold text-slate-500">n/a</span>;
                })()}
                {s.status !== "ended" && s.status !== "vod" && <Btn variant="ghost" onClick={() => end(s)} data-testid={`live-end-${s.id}`}><StopCircle className="w-4 h-4" /> End</Btn>}
                {(s.status === "scheduled" || s.isLive) && <Btn variant="ghost" onClick={() => setNotifyFor(s)} data-testid={`live-notify-${s.id}`}><Bell className="w-4 h-4" /> Notify</Btn>}
                <button onClick={() => edit(s)} data-testid={`live-edit-${s.id}`} title="Edit" className="w-8 h-8 rounded-lg bg-white/5 border border-[#273042] text-slate-300 hover:text-white flex items-center justify-center"><Pencil className="w-4 h-4" /></button>
                {s.vodShowId && <span className="text-[10px] text-emerald-400 flex items-center gap-1"><Film className="w-3 h-3" /> VOD</span>}
                <DeleteBtn onClick={() => del(s.id)} testid={`live-delete-${s.id}`} />
              </div>
            </div>
          ))}
        </Card>
      )}

      <h3 className="text-white font-bold mb-3 flex items-center gap-2"><Tv className="w-5 h-5 text-[#E11D2A]" /> Channels</h3>
      <Card className="p-5 mb-4 grid sm:grid-cols-5 gap-3 items-end">
        <Field label="Channel name" value={chForm.channelName} onChange={(e) => setChForm({ ...chForm, channelName: e.target.value })} data-testid="channel-name-input" />
        <Field label="YouTube @handle" value={chForm.handle} onChange={(e) => setChForm({ ...chForm, handle: e.target.value })} placeholder="@bbkigalifm" />
        <Field label="Channel ID (optional)" value={chForm.channelId} onChange={(e) => setChForm({ ...chForm, channelId: e.target.value })} placeholder="UC…" />
        <Field label="Thumbnail URL" value={chForm.thumbnail} onChange={(e) => setChForm({ ...chForm, thumbnail: e.target.value })} placeholder="Optional" />
        <Btn onClick={addChannel} data-testid="channel-add-btn"><Plus className="w-4 h-4" /> Add channel</Btn>
      </Card>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {channels.map((c, i) => (
          <Card key={c.id || c.key} className="p-4" data-testid={`channel-${c.key}`}>
            <input value={c.channelName || ""} onChange={(e) => setChannels((cs) => cs.map((x, j) => j === i ? { ...x, channelName: e.target.value } : x))} className="w-full bg-transparent text-white font-semibold text-sm outline-none border-b border-transparent focus:border-[#273042]" />
            <input value={c.handle || ""} onChange={(e) => setChannels((cs) => cs.map((x, j) => j === i ? { ...x, handle: e.target.value } : x))} placeholder="@handle" className="mt-2 w-full bg-[#0A0C10] border border-[#273042] rounded-lg px-2 h-9 text-xs text-slate-300 outline-none" />
            <input value={c.channelId || ""} onChange={(e) => setChannels((cs) => cs.map((x, j) => j === i ? { ...x, channelId: e.target.value } : x))} placeholder="Channel ID (UC…)" className="mt-2 w-full bg-[#0A0C10] border border-[#273042] rounded-lg px-2 h-9 text-xs text-slate-300 outline-none" />
            <div className="flex items-center gap-2 mt-3">
              <Btn variant="ghost" onClick={() => saveChannel(c)} data-testid={`channel-save-${c.key}`} className="flex-1"><Save className="w-4 h-4" /> Save</Btn>
              <button onClick={() => delChannel(c)} className="w-9 h-9 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
            </div>
          </Card>
        ))}
      </div>
      <NotifyMembersDialog open={!!notifyFor} type="program" refId={notifyFor?.id} defaultTitle={notifyFor?.title} onClose={() => setNotifyFor(null)} />
    </div>
  );
}
